// src/generators/dzial1_liczby_i_dzialania/temat1_dzialania/podtemat2_wartosc_bezwzgledna_types/AbsSqrtMinusIntValueTask.js

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// ===================== helpers =====================

function toRawAnswer(userAnswer) {
  if (userAnswer == null) return "";
  if (typeof userAnswer === "string" || typeof userAnswer === "number") {
    return String(userAnswer);
  }
  if (typeof userAnswer === "object") {
    return String(
      userAnswer.raw ??
        userAnswer.value ??
        userAnswer.text ??
        userAnswer.input ??
        userAnswer.latex ??
        ""
    );
  }
  return String(userAnswer);
}

function evaluateExpression(expr) {
  const cleaned = String(expr || "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\\\sqrt\{(\d+)\}/g, "√$1")
    .replace(/\\sqrt\{(\d+)\}/g, "√$1")
    .replace(/sqrt\{(\d+)\}/g, "√$1")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1");

  try {
    const parsed = cleaned
      .replace(/(\d*)√(\d+)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      .replace(/√(\d+)/g, (_, b) => `Math.sqrt(${b})`);

    const parsed2 = parsed.replace(
      /\\sqrt\{(\d+)\}/g,
      (_, b) => `Math.sqrt(${b})`
    );
    return Function(`return ${parsed2}`)();
  } catch {
    return NaN;
  }
}

function normalizeCanonical(str) {
  return String(str || "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\√/g, "√")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .replace(/^1√/, "√")
    .replace(/√0*(\d+)/g, (_, d) => `√${parseInt(d, 10)}`)
    .replace(/^0*(\d+)$/, (_, d) => `${parseInt(d, 10)}`)
    .trim();
}

function simplifySqrt(n) {
  let factor = 1;
  for (let i = Math.floor(Math.sqrt(n)); i >= 2; i--) {
    if (n % (i * i) === 0) {
      factor = i;
      break;
    }
  }
  const outside = factor;
  const inside = n / (factor * factor);

  if (inside === 1) return `${outside}`; // nie losujemy kwadratów, więc raczej nie zajdzie
  if (outside === 1) return `\\sqrt{${inside}}`;
  return `${outside}\\sqrt{${inside}}`;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// parsuje latex pierwiastka: \sqrt{m} lub k\sqrt{m}
function parseRootLatex(rootLatex) {
  const s = String(rootLatex || "").replace(/\s/g, "");

  // k\sqrt{m}
  let m = s.match(/^(\d+)\\sqrt\{(\d+)\}$/);
  if (m) return { coeff: parseInt(m[1], 10), inside: parseInt(m[2], 10) };

  // \sqrt{m}
  m = s.match(/^\\sqrt\{(\d+)\}$/);
  if (m) return { coeff: 1, inside: parseInt(m[1], 10) };

  return { coeff: 1, inside: NaN };
}

// ================================ generator ================================

export function generateTask() {
  const nPool = [
    2, 3, 5, 6, 7, 10, 11, 13, 14, 15, 17, 19, 21, 22, 23, 26, 29, 30, 31,
  ];

  const n = pick(nPool);

  // dobieramy a blisko √n, żeby było 50/50 znaku
  const root = Math.sqrt(n);
  const floorR = Math.floor(root);

  const aLess = Math.max(1, floorR - 1);
  const aMore = Math.max(2, floorR + 1);

  const negativeInside = Math.random() < 0.5;
  const a = negativeInside ? aMore : aLess;

  const rootSimpl = simplifySqrt(n);
  const exprLatex = `\\left|${rootSimpl} - ${a}\\right|`;

  const resultLatex = negativeInside ? `${a} - ${rootSimpl}` : `${rootSimpl} - ${a}`;

  // ✅ przejście do „porównywalnej” postaci tylko gdy jest czynnik (np. 2√3 -> √12)
  const { coeff, inside } = parseRootLatex(rootSimpl);
  const sqrtComparableLatex =
    coeff > 1 && Number.isFinite(inside)
      ? `${rootSimpl} = \\sqrt{${coeff * coeff}}\\cdot\\sqrt{${inside}} = \\sqrt{${n}}`
      : "";

  return {
    type: "AbsSqrtMinusIntValueTask",
    data: {
      n,
      a,
      negativeInside,
      exprLatex,
      rootSimpl,
      resultLatex,
      sqrtComparableLatex,
    },
    tags: ["wartość_bezwzględna", "pierwiastki", "działania"],
  };
}

// ================================ rendering ================================

export function renderTask({ data }) {
  const { exprLatex } = data;

  return (
    <div
      className="space-y-3"
      style={{
        textAlign: "left",
        paddingBottom: 18,
        marginBottom: 10,
      }}
    >
      <p style={{ marginBottom: 0 }}>
        Oblicz wartość wyrażenia:
        <span style={{ marginLeft: 14 }}>
          <InlineMath math={exprLatex} />
        </span>
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <SqrtInput
      value={toRawAnswer(userAnswer)}
      onChange={onChange}
      disabled={disabled}
      wide={true}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = toRawAnswer(userAnswer);
  if (!raw || String(raw).trim() === "") return false;

  const expectedNorm = normalizeCanonical(data.resultLatex);
  const givenNorm = normalizeCanonical(raw);

  const expectedVal = evaluateExpression(expectedNorm);
  const givenVal = evaluateExpression(givenNorm);

  if (!Number.isFinite(expectedVal) || !Number.isFinite(givenVal)) return false;

  return Math.abs(expectedVal - givenVal) < 0.01;
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.resultLatex} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = toRawAnswer(userAnswer);
  if (!raw || String(raw).trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={String(raw)} />;
}

// ================================ explanation ================================

export function renderExplanation({ data }) {
  const { n, a, rootSimpl, exprLatex, resultLatex, sqrtComparableLatex } = data;

  const a2 = a * a;

  // jeśli a^2 < n => a < √n => (√n - a) > 0
  // jeśli a^2 > n => √n < a => (√n - a) < 0
  const isPositive = a2 < n;

  // ✅ w tekście porównania pokazujemy √n, a nie „rootSimpl”
  // bo porównanie przez kwadraty robimy najczyściej dla √n
  const compareLatex = isPositive
    ? `${a} < \\sqrt{${n}} \\quad\\Longleftrightarrow\\quad ${a2} < ${n}`
    : `\\sqrt{${n}} < ${a} \\quad\\Longleftrightarrow\\quad ${n} < ${a2}`;

  // ✅ a znak zapisujemy już dla oryginalnego wyrażenia z rootSimpl (żeby uczeń widział to samo co w treści)
  const signLine = isPositive ? `${rootSimpl} - ${a} > 0` : `${rootSimpl} - ${a} < 0`;

  const absStep = isPositive
    ? `\\left|${rootSimpl} - ${a}\\right| = ${rootSimpl} - ${a}`
    : `\\left|${rootSimpl} - ${a}\\right| = ${a} - ${rootSimpl}`;

  const finalLine = `${exprLatex} = ${resultLatex}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong>{" "}
        Porównujemy pierwiastek z liczbą całkowitą (porównujemy kwadraty):

        {/* ✅ pokazujemy przejście tylko, gdy faktycznie jest sens (np. 2√3 -> √12) */}
        {sqrtComparableLatex ? (
          <div style={{ marginTop: 8 }}>
            Najpierw zapisujemy pierwiastek w postaci{" "}
            <InlineMath math={`\\sqrt{${n}}`} />:
            <div style={{ textAlign: "center" }}>
              <BlockMath math={sqrtComparableLatex} />
            </div>
          </div>
        ) : null}

        <div style={{ marginTop: 8 }}>
          Porównujemy <InlineMath math={`\\sqrt{${n}}`} /> z{" "}
          <InlineMath math={`${a}`} />:
          <div style={{ textAlign: "center" }}>
            <BlockMath math={compareLatex} />
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <BlockMath math={signLine} />
        </div>
      </div>

      <div>
        <strong>Krok 2.</strong> Zdejmujemy wartość bezwzględną:
        <div style={{ textAlign: "center" }}>
          <BlockMath math={absStep} />
        </div>
      </div>

      <div>
        <strong>Wniosek.</strong>
        <div style={{ textAlign: "center" }}>
          <BlockMath math={finalLine} />
        </div>
      </div>
    </div>
  );
}
